import React, { useEffect, useState } from "react";
import WhiteButton from "../Common/WhiteButton";
import EventsIcon from "../../assets/icons/Icon_Library/application icons/assets/events.svg2";
import CommunicationIcon from "../../assets/icons/Icon_Library/application icons/settings/communications.svg2";
import AssetsIcon from "../../assets/icons/Icon_Library/application icons/assets/assets.svg2";
import DetailsIcon from "../../assets/icons/Icon_Library/application icons/assets/events/details-01.svg2";
import CompleteIcon from "../../assets/icons/Icon_Library/application icons/assets/events/step_complete-01.svg2";
import MyIcon from "../Common/MyIcon";
import { Button, Modal, ModalBody, Nav, Tab } from "react-bootstrap";
import EventType from "./EventType";
import EventComplete from "./EventComplete";
import {
  SolutionEvent,
  useCreateNoSolutionEventMutation,
  useCreateSolutionEventMutation,
  useGetEventSolutionsQuery,
} from "../store/slices/settingsSlice";
import EventRecipients from "./EventRecipients";
import EventDetails from "./EventDetails";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { toast } from "react-toastify";
import HelpButton from "../HelpContent/HelpButton";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import EventArea from "./EventArea";

const tabs = [
  {
    name: "alert Type",
    heading: "Alert Type",
    subheading:
      "Select the type of event you want to create, such as a warning, alert, or notification, to define how it will be communicated and handled.",
    icon: DetailsIcon,
    helpId: "event-type",
  },
  {
    name: "area",
    heading: "Area",
    subheading:
      "Define the geographic area or zone associated with this event.",
    icon: AssetsIcon,
    helpId: "event-area",
  },
  {
    name: "assets and recipients",
    heading: "Assets and Recipients",
    subheading:
      "Select who should receive this event and which assets are affected.",
    icon: AssetsIcon,
    helpId: "event-recipients",
  },
  {
    name: "communications",
    heading: "Communications",
    subheading:
      "Define the message content, delivery method, and escalation options for this event.",
    icon: CommunicationIcon,
    helpId: "event-details",
  },

  {
    name: "complete",
    heading: "Event Complete",
    subheading:
      "Your event has been created successfully and is ready to be activated or reviewed.",
    icon: CompleteIcon,
    helpId: "event-complete",
  },
];

function CreateEventModal({
  showButton = undefined,
  initialData = undefined,
  eventSolutionUuid: _eventSolutionUuid = undefined,
  from = undefined,
}: {
  showButton?: React.ReactNode;
  initialData?: Partial<SolutionEvent> | undefined;
  eventSolutionUuid?: string;
  from?: "map" | "asset" | "events" | undefined;
}) {
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    setSelectedTab(0);
    setIsEventMinimal(false);
    setEventSolutionUuid("");
    setEvent(initialData || {});
  };
  const handleShow = () => setShow(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [event, setEvent] = useState<Partial<SolutionEvent> | undefined>(
    initialData || {}
  );

  const [affected, setAffected] = useState({ contacts: 0, assets: 0 });

  const [save] = useCreateSolutionEventMutation();

  const [createNoSolutionEvent] = useCreateNoSolutionEventMutation();

  const handleSave = async () => {
    if (event) {
      if (isEventMinimal) {
        const res = await createNoSolutionEvent(event);
        if ("data" in res) {
          toast.success("Event created successfully");
          handleClose();
        }
        return;
      }
      const res = await save(event);
      if ("data" in res) {
        toast.success("Event created successfully");
        handleClose();
      }
    }
  };

  const eventSolutions =
    useGetEventSolutionsQuery(undefined, { skip: !show }).data || [];
  const [eventSolutionUuid, setEventSolutionUuid] = useState(
    _eventSolutionUuid || ""
  );

  useEffect(() => {
    // if there is no event solution, set event as minimal and return
    if (eventSolutions.length === 0) {
      setIsEventMinimal(true);
    } else if (!_eventSolutionUuid && from === "events") {
      setIsEventMinimal(true);
    } else {
      setIsEventMinimal(false);
    }
    // no solution is selected and user came from events page
  }, [eventSolutions, _eventSolutionUuid, from]);

  const [isEventMinimal, setIsEventMinimal] = useState(false);
  const eventSolution = eventSolutions.find(
    (sol) => eventSolutionUuid === sol.Uuid
  );

  useEffect(() => {
    if (eventSolution || isEventMinimal) {
      setEvent({
        ...initialData,
        SolutionClientUuid: eventSolution?.Uuid,
        Name: "New Event",
        AddTicket: true,
        ContactsAndAssetsCriteria: {
          UseAssets: true,
          UseContacts: false,
        },
      });
    }
  }, [eventSolution, isEventMinimal, initialData]);

  return (
    <div>
      <div onClick={handleShow}>
        {showButton || (
          <WhiteButton
            as="span"
            // to="/assets/assets/events/new"
            icon={EventsIcon}
            heading="Add"
            subheading="Event/Alert"
            target={undefined}
            disabled={false}
          />
        )}
      </div>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        contentClassName="bg-purple-gradient"
      >
        <Modal.Header
          className="bg-colored-reverse p-3 rounded-3  rounded-bottom-0"
          closeButton
        >
          <Modal.Title className="text-white">
            <EventsIcon width={30} className="text-white ms-2" />
            <span className="ms-3 fw-bold"> Create an event/alert</span>{" "}
            <HelpButton helpKey="create-event" />
          </Modal.Title>
        </Modal.Header>
        {!!from &&
        ["map", "asset"].includes(from) &&
        !isEventMinimal &&
        !eventSolutionUuid ? (
          <div
            className="p-4 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: "300px" }}
          >
            <div style={{ width: 500 }}>
              <div className="mb-2">
                You need to select a solution to create an event. Please select
                a solution from the dropdown below to proceed with creating your
                event/alert, or select the option to continue without a solution
                to create an event with minimal functionality.
              </div>
            </div>
            <div>
              <div className="d-flex align-items-center gap-5 text-nowrap">
                <div className="w-100" style={{ minWidth: 300 }}>
                  <Input
                    inputType="select"
                    value={[eventSolutionUuid]}
                    onChange={([v]: any) => {
                      setEventSolutionUuid(v);
                      localStorage.setItem("eventsListViewEventSolution", v);
                    }}
                    placeholder="Select Event Solution"
                    options={[
                      ...(eventSolutions.map((sol: any) => ({
                        value: sol.Uuid,
                        label: `${sol.SolutionName} (v${sol.SolutionVersion})`,
                      })) || []),
                    ]}
                  />
                </div>
                OR
                <button
                  onClick={() => setIsEventMinimal(true)}
                  className="btn btn-colored"
                >
                  Continue without a solution
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Modal.Body className="">
              <Tab.Container
                id="data-import-tabs"
                activeKey={selectedTab}
                onSelect={(tab: any) => setSelectedTab(parseInt(tab, 10))}
                mountOnEnter
                unmountOnExit
              >
                {isEventMinimal && (
                  <div className="alert alert-warning">
                    No solution selected. Event is created with minimal
                    functionality.
                  </div>
                )}
                <Nav variant="tabs" className="flex-nowrap align-items-end">
                  <div className="d-flex me-5 mb-3 align-items-center w-75">
                    <div className="fw-bold">
                      <MyIcon
                        Icon={tabs[selectedTab].icon}
                        width={40}
                        className="ms-2 "
                      />
                    </div>
                    <div className="ms-4">
                      <div className="fs-2 fw-bold text-capitalize">
                        {tabs[selectedTab].heading}
                        {tabs[selectedTab].helpId && (
                          <HelpButton helpKey={tabs[selectedTab].helpId} />
                        )}
                      </div>
                      <span className="text-secondary">
                        {tabs[selectedTab].subheading}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow-1"></div>
                  {tabs.map((t, i) => (
                    <Nav.Item>
                      <Nav.Link
                        eventKey={i}
                        className={`fw-bold align-content-center border-0 ${
                          t.name === "area" && from === "asset"
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={t.name === "area" && from === "asset"}
                      >
                        <div className="text-center border-3 pb-1">
                          <t.icon width={20} className="text-purple ms-2" />
                        </div>
                        <div
                          className="text-black fw-bold  mt-2 text-uppercase text-nowrap"
                          style={{ letterSpacing: 2 }}
                        >
                          {t.name}
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey={0}>
                    <EventType event={event} setEvent={setEvent} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={1}>
                    <EventArea event={event} setEvent={setEvent} />
                  </Tab.Pane>
                  <Tab.Pane eventKey={2}>
                    <EventDetails
                      event={event}
                      setEvent={setEvent}
                      affected={affected}
                      setAffected={setAffected}
                      initialData={initialData}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey={3}>
                    <EventRecipients
                      event={event}
                      setEvent={setEvent}
                      isEventMinimal={isEventMinimal}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey={4}>
                    <EventComplete
                      handleSave={handleSave}
                      event={event}
                      setEvent={setEvent}
                      affected={affected}
                      isEventMinimal={isEventMinimal}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Modal.Body>
            <div className="p-3 bg-page">
              <hr className="mt-3 text-secondary" />
              <div className="d-flex justify-content-between">
                <div>
                  {selectedTab !== 0 && (
                    <Button
                      type="submit"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                      onClick={() => setSelectedTab((p) => p - 1)}
                    >
                      BACK
                    </Button>
                  )}
                </div>
                <div>
                  {selectedTab !== 4 ? (
                    <Button
                      type="submit"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                      onClick={() => setSelectedTab((p) => p + 1)}
                    >
                      NEXT
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4 "
                      onClick={handleClose}
                    >
                      CLOSE
                    </Button>
                  )}
                </div>
              </div>
              <JsonCollapse
                json={{
                  event,
                  eventSolutionUuid,
                  from,
                  isEventMinimal,
                  eventSolutions,
                }}
                label="Event"
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default CreateEventModal;
